import classNames from 'class-names';
import { useField } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Form,
  InputGroup,
  Overlay,
  ProgressBar,
  Tooltip,
} from 'react-bootstrap';

import Icon from '../../Atoms/Icon/Icon';

import styles from './password-strength.module.css';
import defaultPasswordOptions, {
  getPasswordMsgs,
  validate,
} from './password-validate';

let defaultsSet = false;
let errorMessages = [];
const progressValue = [];
const variant = [];
const variantMsg = [];

const setDefaults = (passwordOptions) => {
  // Set defaults for error messages.
  const passwordMessages = getPasswordMsgs(passwordOptions);
  errorMessages = [];
  Object.values(passwordMessages).forEach((msg) => {
    if (msg !== '') errorMessages.push(msg);
  });

  // Set defaults for progress bar.
  errorMessages.forEach(() => {
    const progress = 100 * (1 - progressValue.length / errorMessages.length);
    progressValue.push(progress);
    if (progress >= 100) {
      variant.push('success');
      variantMsg.push('strong');
    } else if (progress >= 50) {
      variant.push('warning');
      variantMsg.push('medium');
    } else {
      variant.push('danger');
      variantMsg.push('weak');
    }
  });
  progressValue.push(0);
  variant.push('danger');
  variantMsg.push('');
};

const ProgressLabel = ({ length }) => (
  <small className="text-uppercase">{variantMsg[length]}</small>
);
ProgressLabel.propTypes = {
  length: PropTypes.number,
};

const Required = () => <span className="requiredField">&#42;</span>;

const getClassNames = (success) =>
  success
    ? `text-success ${styles.iconTick}`
    : `text-danger ${styles.iconCross}`;

const PasswordFormControl = ({
  label = null,
  helpText,
  groupClassName,
  labelClassName,
  errorDisplayMode = 'inline',
  errorTooltipPlacement = 'bottom',
  borderless,
  children,
  strengthMeter = false,
  isRequired = false,
  passwordOptions = defaultPasswordOptions,
  ...fieldProps
}) => {
  if (defaultsSet === false) {
    setDefaults(passwordOptions);
    defaultsSet = true;
  }

  const tooltipOverlayTarget = useRef(null);
  const hintOverlayTarget = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [focus, setFocus] = useState(false);
  const [errorKeys, setErrorKeys] = useState(errorMessages);
  const [field, { touched, error }] = useField(fieldProps);
  const isInvalid = touched && !!error;

  useEffect(() => {
    setFocus(fieldProps?.name === document.activeElement?.name);
  }, [document.activeElement]);

  const passKeyUp = async (e) => {
    if (!strengthMeter) return;

    const { errors } = await validate(e.target.value, {
      list: true,
      abortEarly: false,
      context: passwordOptions,
    });
    const result = errors ? Object.values(errors) : [];
    setErrorKeys(result);
  };

  const setTooltipVisibility = () => {
    if (strengthMeter) {
      setFocus(true);
    }
  };

  return (
    <Form.Group controlId={field.name} className={groupClassName}>
      {!!label && (
        <Form.Label className={labelClassName}>
          {label}
          {isRequired && <Required />}
        </Form.Label>
      )}
      <InputGroup>
        <Form.Control
          className={classNames('pr-1', fieldProps.className, {
            'border-0': borderless,
          })}
          type={isVisible ? 'text' : 'password'}
          style={{ borderRight: 'none' }}
          isInvalid={isInvalid}
          ref={tooltipOverlayTarget}
          onClick={(e) => passKeyUp(e) && setTooltipVisibility()}
          onKeyUp={(e) => passKeyUp(e)}
          {...field}
          {...fieldProps}
        />
        <InputGroup.Append>
          <Button
            variant="link"
            onClick={() => setIsVisible(!isVisible)}
            ref={hintOverlayTarget}
            className={classNames(
              'border-left-0 py-0',
              {
                'border-0': borderless,
                'border-danger': isInvalid,
              },
              isInvalid ? 'text-danger' : 'text-gray-500',
            )}
          >
            <Icon
              name={isVisible ? 'eye-filled' : 'eye-slash-filled'}
              className="h4 mb-0"
            />
          </Button>
          {children}
        </InputGroup.Append>
        {isInvalid && errorDisplayMode === 'inline' && (
          <>
            <span className="invalid-feedback">{error}</span>
            {errorKeys.map((err) =>
              !errorMessages.includes(err) ? (
                <span className="invalid-feedback" key={err}>
                  {err}
                </span>
              ) : null,
            )}
          </>
        )}
      </InputGroup>

      <Overlay
        target={hintOverlayTarget.current}
        show={focus && strengthMeter}
        placement="bottom"
      >
        {({ arrowProps: _arrowProps, show: _show, ...props }) => (
          <div
            {...props}
            style={{
              backgroundColor: 'white',
              padding: '2px 10px',
              margin: 0,
              zIndex: 9999,
              borderRadius: 3,
              boxShadow: 'lightgrey 0px 7px 20px',
              ...props.style,
            }}
          >
            <strong className="small font-weight-bold">
              Password must have:
            </strong>
            <ul
              className={classNames('px-1 small', styles.passwordStrengthMeter)}
            >
              {errorMessages.map((err) => (
                <li
                  key={err}
                  className={classNames(
                    getClassNames(!errorKeys.includes(err)),
                  )}
                >
                  {err}
                </li>
              ))}
              <div className="mt-2">
                <strong>Strength:</strong>
                <ProgressBar
                  label={<ProgressLabel length={errorKeys.length} />}
                  variant={variant[errorKeys.length]}
                  now={progressValue[errorKeys.length] || 0}
                />
              </div>
            </ul>
          </div>
        )}
      </Overlay>

      <Overlay
        target={tooltipOverlayTarget.current}
        show={isInvalid && errorDisplayMode === 'tooltip'}
        placement={errorTooltipPlacement}
      >
        <Tooltip className="tooltip-red">{error}</Tooltip>
      </Overlay>

      {helpText && <Form.Text muted>{helpText}</Form.Text>}
    </Form.Group>
  );
};

PasswordFormControl.propTypes = {
  style: PropTypes.instanceOf(React.CSSProperties),
  strengthMeter: PropTypes.bool,
  borderless: PropTypes.bool,
  isRequired: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  errorDisplayMode: PropTypes.oneOf(['inline', 'tooltip']),
  errorTooltipPlacement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  groupClassName: PropTypes.string,
  helpText: PropTypes.string,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  passwordOptions: PropTypes.shape({
    minChars: PropTypes.number,
    minUpperCaseChars: PropTypes.number,
    minLowerCaseChars: PropTypes.number,
    minNumbers: PropTypes.number,
    minSpecialChars: PropTypes.number,
    noSpaces: PropTypes.bool,
  }),
};

export default PasswordFormControl;
